// Layerul «Realist» al scenei 3D: materiale, lumină, ceață, muchii.
//
// Layerul CLASIC vopsește fiecare familie cu culoarea ei (albastru = bară,
// roșu = perete structural) și luminează totul la fel: e o legendă
// tridimensională, și pentru asta e bun. Ce nu poate face e să arate ce e
// clădirea — beton cu armătură în el.
//
// «Realist» răstoarnă raportul: betonul devine unul singur, translucid, cu
// muchia trasată; armătura devine singurul lucru plin (v. `armatura3d.ts`).
// Ca să nu iasă o ceață de sticlă, patru lucruri trebuie să fie adevărate în
// același timp, și fiecare a fost obținut cu preț mic pe o mașină slabă:
//
//   1. O SINGURĂ LUMINĂ CHEIE. Scena clasică are ambianță 2,15 — totul e la
//      fel de luminat, deci nimic n-are volum. Aici: o direcțională care
//      modelează, o emisferică rece de umplere, ambianță mică.
//   2. MUCHIILE, TRASATE. Un volum la 12 % opacitate fără muchii nu se mai
//      citește ca volum. Muchiile fiecărei familii se topesc într-o SINGURĂ
//      geometrie (un desen, nu opt sute), altfel numărul de desene ar crește
//      cu numărul de elemente.
//   3. CEAȚĂ DE ADÂNCIME. Fără ea, muchiile din spate cântăresc cât cele din
//      față și clădirea se aplatizează — cu atât mai rău cu cât are mai
//      multe etaje.
//   4. UN MEDIU DE REFLEXIE. Fără `envMap`, un material lucios arată exact
//      ca unul mat: reflexul de la unghi razant (ce face sticla să pară
//      sticlă) vine din mediu, nu din lumini.
//
// Ce NU folosește, deliberat: `transmission` (refracție reală) — cere o
// trecere de randare în plus la fiecare cadru, adică exact ce nu-și permite
// un laptop fără plăcă video.

import * as THREE from "three";
import type { Cheie } from "../i18n";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

export type Layer3D = "clasic" | "realist";

export const LAYERE: { cheie: Layer3D; nume: Cheie }[] = [
  { cheie: "clasic", nume: "struct.layere.clasic" },
  { cheie: "realist", nume: "struct.layere.realist" },
];

/** Layerul e o preferință de VEDERE, nu o setare de calcul: nu are ce căuta
 *  în preferințele proiectului (v. „ce e o setare și ce nu e"). Stă în
 *  browserul omului, pe proiect, ca proiecția 2D/3D. */
const CHEIE = (proiect: string) => `ob.layer3d.${proiect}`;

export function layerSalvat(proiect: string): Layer3D {
  try {
    const v = localStorage.getItem(CHEIE(proiect));
    if (v === "clasic" || v === "realist") return v;
  } catch {
    /* Safari în navigare privată aruncă la citire — nu e un motiv să cadă
       scena; se cade pe implicit. */
  }
  return "realist";
}

/** Layerul pe care îl desenează scena: ce a ales omul în ea, altfel
 *  «Clasic» pe un rând fără armare (realistul n-ar avea armătură de arătat),
 *  altfel alegerea salvată pe proiect. Implicitul nu se salvează: pe o
 *  soluție armată se revine la alegerea proiectului. */
export function layerDeAfisat(ales: Layer3D | null, salvat: Layer3D,
                              faraArmare: boolean | undefined): Layer3D {
  return ales ?? (faraArmare ? "clasic" : salvat);
}

export function salveazaLayer(proiect: string, layer: Layer3D) {
  try {
    localStorage.setItem(CHEIE(proiect), layer);
  } catch {
    /* v. mai sus */
  }
}

// --- paleta layerului -------------------------------------------------------
//
// Un singur beton, o singură zidărie. Muchia e RIDICATĂ la un gri mediu, nu
// coborâtă la negru: dacă muchia betonului e închisă, se bate cu armătura și
// nu se mai vede care e care.

export const REALIST = {
  fundalSus: "#e9eef3",
  fundalJos: "#94a2b0",
  ceata: "#c3cdd6",
  beton: 0x8794a1,
  betonMuchie: 0x505d68,
  placa: 0x7d8a96,
  placaMuchie: 0x4c5862,
  /** ZIDĂRIA e GRI, ca betonul, și abia se vede. O nuanță caldă o scoate
   *  din fundal, dar o scoate și din clădire: ochiul citește întâi maroul
   *  pereților, adică exact ce NU e subiectul desenului. Scena are un singur
   *  material cromatic — aurul selecției; tot restul e gri. */
  zid: 0xa3aeb8,
  zidMuchie: 0x78838d,
  fundatie: 0x8a95a0,
  fundatieMuchie: 0x6b757e,
  otel: 0x2f343a,
  otelEtrier: 0x272b30,
  otelPlasa: 0x3d444b,
  /** Bara care se PETRECE cu alta. Aramiul o desparte de restul oțelului
   *  fără să iasă din familia lui — nu e un alt material, e aceeași bară
   *  într-un loc care se citește: unde se încheie una și începe cealaltă. */
  otelInnadire: 0xa85c28,
  /** Oțelul care NU e evidențiat, cât timp altceva e: rămâne pe ecran, ca
   *  să se vadă UNDE e ce s-a ales, dar se retrage — apropiat de beton, ca
   *  aurul selecției să fie singurul lucru plin din scenă. */
  otelStins: 0x9aa3ab,
  selectat: 0xe8a33a,
} as const;

// CÂT DE MULT SE VEDE FIECARE FAMILIE. Placa e o suprafață mare a clădirii:
// la opacitatea la care se pierde conturul ei, modelul arată ca un schelet
// care plutește. Zidăria însă stă PESTE structură, în fiecare cameră: urcată,
// acoperă tocmai cadrul pentru care există layerul. Rămâne un văl prin care
// se ghicește unde e perete și unde e gol, nu o suprafață care se citește.
const OPACITATE = {
  stalp: 0.44, grinda: 0.40, placa: 0.30, balcon: 0.34,
  pereteBeton: 0.40, zid: 0.16, fundatie: 0.36,
} as const;

export type FamilieRealist = keyof typeof OPACITATE;

/** Materialul de beton al unei familii. `MeshStandardMaterial`, nu
 *  `MeshPhysical`: reflexul vine din `envMap`, care e gratis odată generat,
 *  iar `transmission` ar costa o trecere de randare pe cadru. */
export function materialRealist(fam: FamilieRealist): THREE.MeshStandardMaterial {
  const placa = fam === "placa" || fam === "balcon";
  return new THREE.MeshStandardMaterial({
    color: fam === "zid" ? REALIST.zid
      : fam === "fundatie" ? REALIST.fundatie
        : placa ? REALIST.placa : REALIST.beton,
    transparent: true,
    opacity: OPACITATE[fam],
    roughness: fam === "zid" ? 0.55 : 0.28,
    metalness: 0.0,
    envMapIntensity: fam === "zid" ? 0.55 : 0.95,
    depthWrite: false,
    side: THREE.DoubleSide,
    // DOUĂ FEȚE, O SINGURĂ TRECERE. Un material translucid cu `DoubleSide`
    // e desenat de three în două treceri (întâi fețele din spate, apoi cele
    // din față), ca amestecul să fie corect — adică DE DOUĂ ORI numărul de
    // desene, pentru o clădire care are deja o mie de piese. Măsurat pe
    // blocul de referință: 1.819 desene pe cadru față de 948. Diferența de
    // amestec nu se vede pe fețe plate de beton; costul se vede pe orice
    // laptop fără plăcă video.
    forceSinglePass: true,
  });
}

const CULOARE_MUCHIE: Record<FamilieRealist, number> = {
  stalp: REALIST.betonMuchie,
  grinda: REALIST.betonMuchie,
  pereteBeton: REALIST.betonMuchie,
  placa: REALIST.placaMuchie,
  balcon: REALIST.placaMuchie,
  zid: REALIST.zidMuchie,
  fundatie: REALIST.fundatieMuchie,
};

// Muchia SUSȚINE volumul, nu îl înlocuiește. La opacitate mare, o clădire cu
// o mie de piese devine un ghem de sârmă în care betonul nu se mai vede —
// exact ce se întâmplă dacă muchiile urcă peste ~0,5.
const OPACITATE_MUCHIE: Record<FamilieRealist, number> = {
  stalp: 0.50, grinda: 0.46, pereteBeton: 0.48,
  placa: 0.44, balcon: 0.44, zid: 0.24, fundatie: 0.42,
};

/** Muchiile unui grup întreg, într-o SINGURĂ geometrie.
 *
 *  `EdgesGeometry` per mesh ar însemna 900 de obiecte în plus în scenă și
 *  900 de desene pe cadru. Aici se calculează o dată, la construcție, se
 *  aduc în coordonate globale și se lipesc: un `LineSegments`, un desen.
 *  Pragul de 25° păstrează muchiile reale ale cutiei și aruncă liniile de
 *  triangulare ale plăcilor extrudate.
 */
/** Sub atâta, o muchie nu mai descrie forma — o aglomerează. Capetele unui
 *  stâlp de 30×60 sunt patru laturi de sub o jumătate de metru; desenate,
 *  fiecare stâlp aduce opt liniuțe care nu spun nimic, iar o clădire cu o
 *  mie de piese devine un ghem de sârmă prin care betonul nu se mai vede.
 *  Ce rămâne sunt muchiile LUNGI: exact cele care dau forma. */
const MUCHIE_MIN_M = 0.5;

export function muchiiFamilie(
  grup: THREE.Object3D, fam: FamilieRealist,
): THREE.LineSegments | null {
  const pozitii: number[] = [];
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  grup.updateWorldMatrix(true, true);
  grup.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    const eg = new THREE.EdgesGeometry(m.geometry, 25);
    const p = eg.getAttribute("position") as THREE.BufferAttribute;
    for (let i = 0; i + 1 < p.count; i += 2) {
      a.fromBufferAttribute(p, i).applyMatrix4(m.matrixWorld);
      b.fromBufferAttribute(p, i + 1).applyMatrix4(m.matrixWorld);
      if (a.distanceTo(b) < MUCHIE_MIN_M) continue;
      pozitii.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }
    eg.dispose();
  });
  if (!pozitii.length) return null;
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position",
    new THREE.Float32BufferAttribute(pozitii, 3));
  const ls = new THREE.LineSegments(geo, new THREE.LineBasicMaterial({
    color: CULOARE_MUCHIE[fam],
    transparent: true,
    opacity: OPACITATE_MUCHIE[fam],
    depthWrite: false,
  }));
  ls.userData.muchii = fam;
  return ls;
}

/** Cine ARUNCĂ umbra: structura de deasupra terenului.
 *
 *  Nu infrastructura — ea e sub planul care primește umbra, deci n-ar avea
 *  unde s-o pună —, și nu zidăria, care e context. Umbra unei clădiri
 *  translucide iese oricum plină (harta de umbră nu știe de transparență):
 *  tocmai de asta e ținută la o opacitate mică, cât să AȘEZE clădirea pe
 *  teren, nu s-o dubleze cu o pată. */
export function aruncaUmbra(grup: THREE.Object3D) {
  grup.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh) m.castShadow = true;
  });
}

/** Fundalul: un gradient vertical, ca cerul dintr-un studio. O culoare plată
 *  face clădirea să plutească; gradientul îi dă un sus și un jos. */
/** Fundalul modului CLASIC: același gradient vertical, în oțelul rece al
 *  temei — sus aproape alb, jos un gri-albastru abia vizibil, ca
 *  modelul să stea pe ceva, nu să plutească pe o foaie. */
export function fundalClasic(): THREE.Texture {
  return gradientVertical("#f6f8fa", "#e6ebf0");
}

function gradientVertical(sus: string, jos: string): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = 2;
  c.height = 256;
  const ctx = c.getContext("2d")!;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, sus);
  g.addColorStop(1, jos);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 2, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export function fundalRealist(): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = 2;
  c.height = 256;
  const ctx = c.getContext("2d")!;
  const g = ctx.createLinearGradient(0, 0, 0, 256);
  g.addColorStop(0, REALIST.fundalSus);
  g.addColorStop(1, REALIST.fundalJos);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 2, 256);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export type MediulRealist = {
  env: THREE.Texture;
  dispose: () => void;
};

/** Mediul de reflexie. `RoomEnvironment` e o cameră de studio construită din
 *  câteva cutii luminoase — se generează o dată, în ~15 ms, și nu mai costă
 *  nimic după aceea. */
export function mediuRealist(renderer: THREE.WebGLRenderer): MediulRealist {
  const pmrem = new THREE.PMREMGenerator(renderer);
  const env = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
  pmrem.dispose();
  return { env, dispose: () => env.dispose() };
}

/** Lumina, ceața și planul care primește umbra.
 *
 *  UMBRA SE CALCULEAZĂ O DATĂ. Clădirea nu se mișcă — doar camera —, deci
 *  harta de umbră n-are de ce să se refacă la fiecare cadru:
 *  `autoUpdate = false`, iar cine schimbă ce se vede cere o singură
 *  reîmprospătare. Fără asta, un model cu o mie de piese se desenează de
 *  două ori pe cadru și rotirea devine sacadată.
 */
export function luminiRealist(
  scene: THREE.Scene, renderer: THREE.WebGLRenderer,
  centru: THREE.Vector3, span: number, zJos: number,
): { ceruUmbra: () => void; dispose: () => void } {
  // `zJos` e COTA TERENULUI (baza structurii), nu fundul cutiei — v. planul
  // de mai jos.
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.96;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.shadowMap.autoUpdate = false;

  // ceața pornește DINCOLO de clădire: pusă mai aproape, spală și fața din
  // față, iar modelul iese lăptos în loc de adânc
  scene.fog = new THREE.Fog(new THREE.Color(REALIST.ceata).getHex(),
    span * 1.4, span * 4.6);

  const amb = new THREE.HemisphereLight(0xdfe9f2, 0x8d949a, 0.62);
  scene.add(amb);
  const key = new THREE.DirectionalLight(0xfff6e8, 2.9);
  key.position.set(centru.x - span * 0.9, centru.y - span * 1.25,
    centru.z + span * 1.5);
  key.target.position.copy(centru);
  key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024);
  const r = span * 0.85;
  const cam = key.shadow.camera as THREE.OrthographicCamera;
  cam.left = -r; cam.right = r; cam.top = r; cam.bottom = -r;
  cam.near = 0.1; cam.far = span * 4;
  cam.updateProjectionMatrix();
  key.shadow.bias = -0.0012;
  key.shadow.normalBias = 0.03;
  scene.add(key);
  scene.add(key.target);

  // PLANUL CARE PRIMEȘTE UMBRA stă la COTA TERENULUI, nu la fundul cutiei.
  // Sub cota aia e infrastructura — talpa, elevațiile, grinzile de fundare —,
  // iar o umbră căzută acolo ar rămâne ascunsă chiar de betonul pe care
  // tocmai vrem să-l arătăm. La cota terenului, umbra cade în jurul clădirii,
  // unde cade și în realitate, iar fundația se vede în continuare prin plan:
  // `ShadowMaterial` nu desenează nimic în afară de umbra căzută pe el.
  const sol = new THREE.Mesh(
    new THREE.PlaneGeometry(span * 4, span * 4),
    new THREE.ShadowMaterial({ opacity: 0.30 }),
  );
  sol.position.set(centru.x, centru.y, zJos - 0.01);
  sol.receiveShadow = true;
  sol.userData.solUmbra = true;
  scene.add(sol);

  return {
    ceruUmbra: () => { renderer.shadowMap.needsUpdate = true; },
    dispose: () => {
      sol.geometry.dispose();
      (sol.material as THREE.Material).dispose();
      scene.fog = null;
      renderer.shadowMap.enabled = false;
      renderer.shadowMap.autoUpdate = true;
      renderer.toneMapping = THREE.NoToneMapping;
    },
  };
}
